import FunnelLayout from "@components/quiz/FunnelLayout"
import FunnelLogo from "@components/quiz/FunnelLogo"
import { setQuizAnswer } from "@helpers/quizAnswers"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { Trans, useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ONBOARDING_BASE_PATH, QUIZ_STEPS } from "./quizSteps"
import trackQuizAnswer from "./trackQuizAnswer"

const AGE_OPTIONS = [
  { background: "bg-vm-primary-soft", emoji: "🧑‍🎓", id: "18-24" },
  { background: "bg-vm-yellow-soft", emoji: "👩‍💻", id: "25-34" },
  { background: "bg-vm-green-soft", emoji: "👨‍💼", id: "35-44" },
  { background: "bg-vm-red-soft", emoji: "👩‍🏫", id: "45+" },
]

export default function LandingPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleSelect = (ageId: string) => {
    setQuizAnswer("age", [ageId])
    trackQuizAnswer({
      answers: [ageId],
      question: t("pages.onboarding.landing.heading", { lng: "en" }),
      stepIndex: -1,
    })
    navigate(`${ONBOARDING_BASE_PATH}/${QUIZ_STEPS[0].id}${location.search}`)
  }

  return (
    <FunnelLayout
      header={
        <header className="vm-container flex h-16 items-center justify-between">
          <FunnelLogo />
          <Link
            className="text-sm font-semibold text-vm-ink hover:opacity-70"
            to="/account/sign-in"
          >
            {t("shared.signIn")}
          </Link>
        </header>
      }
    >
      <div className="flex grow flex-col gap-6 pb-8 pt-4">
        <div className="animate-fade-up space-y-3 text-center">
          <h1 className="vm-heading text-[28px] sm:text-[32px]">
            <Trans
              components={{ colored: <span className="vm-highlight" /> }}
              i18nKey="pages.onboarding.landing.heading"
            />
          </h1>
          <p className="text-sm font-bold uppercase tracking-widest text-vm-muted">
            {t("pages.onboarding.landing.quizLength")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {AGE_OPTIONS.map((option, index) => (
            <button
              className="group flex animate-fade-up flex-col overflow-hidden rounded-card border border-vm-line bg-vm-surface shadow-card transition-[transform,box-shadow] duration-200 hover:shadow-card-hover active:scale-[0.97]"
              key={option.id}
              onClick={() => handleSelect(option.id)}
              style={{ animationDelay: `${150 + index * 80}ms` }}
              type="button"
            >
              <span
                className={`flex aspect-[4/3] items-center justify-center text-6xl transition-transform duration-300 group-hover:scale-105 ${option.background}`}
              >
                <span
                  className="animate-float"
                  style={{ animationDelay: `${index * 400}ms` }}
                >
                  {option.emoji}
                </span>
              </span>
              <span className="flex items-center justify-between bg-vm-primary-solid px-3 py-3 text-sm font-semibold text-white">
                {t("pages.onboarding.landing.age", { age: option.id })}
                <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </button>
          ))}
        </div>

        <p className="mt-auto animate-fade-in text-center text-xs leading-relaxed text-vm-muted [animation-delay:600ms]">
          <Trans
            components={{
              privacyLink: (
                <Link
                  className="underline"
                  target="_blank"
                  to="https://www.visualmind.app/privacy.html"
                />
              ),
              termsLink: (
                <Link
                  className="underline"
                  target="_blank"
                  to="https://www.visualmind.app/terms.html"
                />
              ),
            }}
            i18nKey="pages.onboarding.landing.legal"
          />
        </p>
      </div>
    </FunnelLayout>
  )
}
